import { makeStyles } from "tss-react/mui";
import NavBar from "./NavBar";
import TopBar from "./TopBar";
import InfoBar from "./InfoBar";
import React, { useState, lazy, useEffect, Suspense, useRef } from "react";
import AdminGuard from "src/components/AdminGuard";
import useMediaQuery from "@mui/material/useMediaQuery";

import { Helmet } from "react-helmet";
import { ZEC } from "../../constants";
import { TimeoutLogic } from "src/components/TimeoutLogic";
import useSelectedSubAccount from "src/hooks/useSelectedSubAccount";
import { useUnleashContext, useFlag } from "@unleash/proxy-client-react";
import useAuth from "src/hooks/useAuth";
import useSideNav from "src/hooks/useSideNav";

import LoadingScreen from "src/components/LoadingScreen";
import { Route, useLocation, useNavigate, Routes, Navigate } from "react-router-dom";
import "@ux-engineering/metal/css/metal.css";
import ApprovalsView from "src/views/reports/ApprovalsView";
import AlertsView from "src/views/reports/AlertsView";
import AlertsManagementView from "src/views/reports/AlertsManagementView";
import userflow from "userflow.js";
import TermsOfServiceDialog from "src/components/TermsOfServiceDialog";
import TrustCenterDashboard from 'src/views/reports/TrustCenterView';
import ViewPaymentsGuard from 'src/components/ViewPaymentsGuard';

// components for child routes of dashboard layout - defined outside component to avoid recreating on every render
const AppRedirectionComp = () => <Navigate to="admin/pool-statistics/pool-hashrate" />;
const PoolHashrateComp = lazy(() => import("src/views/reports/PoolHashrateView"));
const AccountOverviewComp = lazy(() => import("src/views/reports/AccountOverview"));
const BlocksComp = lazy(() => import("src/views/reports/BlocksView"));
const PoolDailyHashrateComp = lazy(() => import("src/views/reports/PoolDailyHashrateView"));
const AllSubAccountsComp = lazy(() => import("src/views/reports/AllSubAccountsView"));
const AllSubAccountsFinancialComp = lazy(() =>
    import("src/views/reports/AllSubAccountsFinancialView")
);
const GroupUserComp = lazy(() => import("src/views/reports/GroupUserView"));
const SubAccountsComp = lazy(() => import("src/views/reports/SubAccountsView"));
const FinancialOverviewComp = lazy(() => import("src/views/reports/FinancialOverview"));
const UserManagementComp = lazy(() => import("src/views/reports/UserManagementView"));
const SubAccountDetailComp = lazy(() => import("src/views/reports/SubAccountDetailView"));
const AuditLogView = lazy(() => import("src/views/reports/AuditLogView"));
const BulkReportsViewComp = lazy(() => import("src/views/reports/BulkReportsView"));
const TermsOfService = lazy(() => import("src/views/reports/TermsOfService"));

const useStyles = makeStyles()(theme => ({
    root: {
        backgroundColor: theme.palette.background.dark,
        display: "flex",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        position: "relative",
    },
    contentContainer: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
    },
    contentContainerMobile: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        padding: "0",
    },
    content: {
        flex: "1 1 auto",
        height: "100%",
        overflow: "auto",
        [theme.breakpoints.down("md")]: {
            paddingBottom: 0,
        },
    },
    metalContent: {
        flex: "1 1 auto",
        height: "100%",
        overflow: "auto",
        paddingLeft: 0,
    },
    redesignBackground: {
        backgroundColor: "var(--neutral-1)",
    },
    wrapper: {
        backgroundColor: "var(--neutral-1)",
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        paddingTop: 146,
        [theme.breakpoints.up("md")]: {
            paddingLeft: 230,
        },
    },
    reportsWrapper: {
        [theme.breakpoints.down("lg")]: {
            paddingTop: "186px",
        },
    },
}));

const DashboardLayout = () => {
    function handleCoinUnitChange(newValue) {
        sessionStorage.setItem("selectedCoinUnit", newValue);
        setCoinUnit(newValue);
    }

    function getInitialCoinUnitState() {
        let initialCoinUnitState = ZEC;
        if (sessionStorage.getItem("selectedCoinUnit")) {
            initialCoinUnitState = sessionStorage.getItem("selectedCoinUnit");
        }
        return initialCoinUnitState;
    }

    const { classes, cx } = useStyles();
    const coin = ZEC;
    const navigate = useNavigate();
    const location = useLocation();
    const [coinUnit, setCoinUnit] = useState(getInitialCoinUnitState());
    const { handleSelectedSubAccountNavUpdate } = useSelectedSubAccount();
    const { loggedInUser } = useAuth();
    const updateUnleashContext = useUnleashContext();
    const [pendingRequestsLength, setPendingRequestsLength] = useState(null);
    const bulkSubAccountExport = useFlag("bulk_sub_account_export");

    // Current TOS version - update this when TOS is updated
    const CURRENT_TOS_VERSION = "2025-04-01";

    // Check if TOS is valid (either current version or valid MSA)
    const isTosValid = accepted_tos_version => {
        if (!accepted_tos_version) return false;

        // Check if it's the current version
        if (accepted_tos_version === CURRENT_TOS_VERSION) return true;

        // Check if it's an MSA format "MSA Expires MM/DD/YYYY"
        const msaMatch = accepted_tos_version.match(/MSA Expires (\d{2}\/\d{2}\/\d{4})/);
        if (msaMatch) {
            const expirationDate = new Date(msaMatch[1]);
            return expirationDate > new Date();
        }

        return false;
    };

    // Check if any group needs TOS acceptance - only calculate once on mount
    const needsTermsAcceptanceRef = useRef(null);
    if (needsTermsAcceptanceRef.current === null) {
        needsTermsAcceptanceRef.current =
            loggedInUser?.groups?.some(groupItem => {
                const isMiningAccountOwner = groupItem.userRole.roleName === "mining_account_owner";
                const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
                return isMiningAccountOwner && !tosIsValid;
            }) || false;
    }

    const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(needsTermsAcceptanceRef.current);
    const { fullscreenMode } = useSideNav();
    const isDesktop = useMediaQuery("(min-width:900px)");

    const handleCloseDialog = () => {
        setIsTermsDialogOpen(false);
    };

    const prevLocationRef = useRef();
    useEffect(() => {
        // Only run if pathname actually changed
        if (prevLocationRef.current === location.pathname) {
            return;
        }
        prevLocationRef.current = location.pathname;

        if (
            location &&
            location.pathname?.match("sub-accounts") &&
            location.state?.view !== "transactions"
        ) {
            handleSelectedSubAccountNavUpdate("0");
        } else {
            handleSelectedSubAccountNavUpdate("4");
            if (location.state?.view === "transactions") {
                navigate(`${location.pathname}/transactions`, {
                    replace: true,
                });
            }
        }
        window.scrollTo(0, 0);
    }, [location, handleSelectedSubAccountNavUpdate, navigate]);

    // Update Unleash context with user's group name when they login
    const userId = loggedInUser?.id;
    useEffect(() => {
        if (!userId || !loggedInUser) return;
        const groupId = sessionStorage.getItem("groupId");
        updateUnleashContext({ userId: groupId });
        userflow.identify(userId, {
            name: loggedInUser.fullName,
            email: loggedInUser.emailAddress,
            signed_up_at: loggedInUser.createdAt,
        });
    }, [userId, loggedInUser, updateUnleashContext]);

    const heapDataCollection = useFlag("heap_data_collection");
    const HEAP_APP_ID = process.env.REACT_APP_HEAP_APP_ID;

    return (
        <div className={cx(classes.root, classes.redesignBackground)}>
            <TimeoutLogic />
            <TopBar coinUnit={coinUnit} onCoinUnitSelectionChange={handleCoinUnitChange} />
            <InfoBar />
            <NavBar
                pendingRequestsLength={pendingRequestsLength}
                setPendingRequestsLength={setPendingRequestsLength}
            />
            <div
                className={
                    location.pathname === "/app/reports"
                        ? cx(classes.wrapper, classes.reportsWrapper)
                        : classes.wrapper
                }
                style={{
                    paddingLeft: fullscreenMode ? "0" : undefined,
                    transition: "padding-left 0.3s ease-in-out",
                }}
            >
                <div
                    style={
                        location.pathname === "/app/account-overview" ||
                            location.pathname === "/app/admin/pool-statistics/pool-hashrate" ||
                            location.pathname === "/app/admin/pool-statistics/pool-daily-hashrate" ||
                            location.pathname === "/app/admin/pool-statistics/blocks" ||
                            location.pathname === "/app/admin/all-sub-accounts" ||
                            location.pathname === "/app/admin/group-user"
                            ? { paddingRight: 0 }
                            : {}
                    }
                    className={
                        isDesktop ? classes.contentContainer : classes.contentContainerMobile
                    }
                >
                    <div
                        className={
                            location.pathname === "/app/account-overview" ||
                                location.pathname === "/app/admin/pool-statistics/pool-hashrate" ||
                                location.pathname ===
                                "/app/admin/pool-statistics/pool-daily-hashrate" ||
                                location.pathname === "/app/admin/pool-statistics/blocks" ||
                                location.pathname === "/app/admin/all-sub-accounts" ||
                                location.pathname === "/app/admin/group-user"
                                ? classes.metalContent
                                : classes.content
                        }
                    >
                        <Suspense fallback={<LoadingScreen />}>
                            <Routes>
                                <Route path="account-overview" element={<AccountOverviewComp />} />
                                <Route
                                    path="sub-accounts/:id/*"
                                    element={
                                        <SubAccountDetailComp
                                            coinUnit={coinUnit}
                                            onCoinUnitSelectionChange={handleCoinUnitChange}
                                        />
                                    }
                                />
                                <Route path="sub-accounts" element={<SubAccountsComp />} />
                                <Route path="user-management" element={<UserManagementComp />} />
                                {!bulkSubAccountExport && (
                                    <Route
                                        path="financial-overview"
                                        element={
                                            <FinancialOverviewComp
                                                coinUnit={coinUnit}
                                                onCoinUnitSelectionChange={handleCoinUnitChange}
                                            />
                                        }
                                    />
                                )}
                                {bulkSubAccountExport && (
                                    <Route path="reports" element={<BulkReportsViewComp />} />
                                )}
                                <Route
                                    path="approvals"
                                    element={
                                        <ApprovalsView
                                            pendingRequestsLength={pendingRequestsLength}
                                            setPendingRequestsLength={setPendingRequestsLength}
                                        />
                                    }
                                /><Route element={<ViewPaymentsGuard />}>
                                    <Route path='audit-log' element={<AuditLogView />} />
                                    <Route path='trust-center' element={<TrustCenterDashboard />} />
                                </Route>
                                <Route path="alerts/active" element={<AlertsView />} />
                                <Route path="alerts/manage" element={<AlertsManagementView />} />
                                <Route
                                    path="help/faqs"
                                    Component={() => {
                                        window.open("https://faq.foundryusapool.com", "_blank");
                                        navigate(-1);
                                        return null;
                                    }}
                                />
                                <Route
                                    path="help/api-documentation"
                                    Component={() => {
                                        window.open("https://api.zcashpool.foundrydigital.com", "_blank");
                                        navigate(-1);
                                        return null;
                                    }}
                                />
                                <Route path="help/terms-of-service" element={<TermsOfService />} />
                                {/* <Route
                                    path="help/status"
                                    Component={() => {
                                        window.open("https://status.foundryusapool.com", "_blank");
                                        navigate(-1);
                                        return null;
                                    }}
                                /> */}
                                {/* Admin access views */}
                                <Route element={<AdminGuard />}>
                                    <Route
                                        path="admin/pool-statistics/pool-hashrate"
                                        element={<PoolHashrateComp />}
                                    />
                                    <Route
                                        path="admin/pool-statistics/blocks"
                                        element={<BlocksComp />}
                                    />
                                    <Route
                                        path="admin/pool-statistics/pool-daily-hashrate"
                                        element={<PoolDailyHashrateComp />}
                                    />
                                    <Route
                                        path="admin/all-sub-accounts/:id/*"
                                        element={
                                            <SubAccountDetailComp
                                                coinUnit={coinUnit}
                                                onCoinUnitSelectionChange={handleCoinUnitChange}
                                            />
                                        }
                                    />
                                    <Route
                                        path="admin/all-sub-accounts"
                                        element={<AllSubAccountsComp />}
                                    />
                                    <Route path="admin/group-user" element={<GroupUserComp />} />
                                </Route>
                                {/* entry point */}
                                <Route index element={<AppRedirectionComp />} />
                                <Route path="*" element={<Navigate to="/404 " />} />
                            </Routes>
                        </Suspense>
                    </div>
                </div>
            </div>

            <Helmet>
                <script src="https://cy3d3zcbp37t.statuspage.io/embed/script.js"></script>
                <script
                    type="text/javascript"
                    id="hs-script-loader"
                    async
                    defer
                    src="//js.hs-scripts.com/8422806.js"
                ></script>
                {heapDataCollection && (
                    <script type="text/javascript">
                        {`
              window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
              heap.load("${HEAP_APP_ID}");
            `}
                    </script>
                )}
            </Helmet>
            <TermsOfServiceDialog open={isTermsDialogOpen} onClose={handleCloseDialog} />
        </div>
    );
};

export default DashboardLayout;
